import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

export default class extends Controller {
  static outlets = ["asset-manager"]
  static values = {
    handle: { type: String, default: ".drag-handle" },
    animation: { type: Number, default: 150 }
  }

  #sortable = null

  connect() {
    this.#initializeSortable()
  }

  disconnect() {
    this.#sortable?.destroy()
    this.#sortable = null
  }

  #initializeSortable() {
    this.#sortable = Sortable.create(this.element, {
      animation: this.animationValue,
      handle: this.handleValue,
      ghostClass: "opacity-50",
      chosenClass: "bg-blue-100",
      dragClass: "shadow-lg",
      filter: "[data-controller='folder-item']", // Don't drag folders
      preventOnFilter: false, // Allow clicking folders

      onStart: (evt) => {
        this.#handleDragStart(evt)
      },

      onEnd: (evt) => {
        this.#handleDragEnd(evt)
      }
    })
  }

  #handleDragStart(evt) {
    const draggedItem = evt.item
    const assetId = draggedItem.dataset.assetItemAssetIdValue

    if (!this.hasAssetManagerOutlet) return

    // Determine which items are being dragged
    const selectedIds = this.assetManagerOutlet.selectedIds
    const dragIds = selectedIds.includes(assetId) && selectedIds.length > 1
      ? selectedIds
      : [assetId]

    // Tell asset-manager what's being dragged (folder-item reads this on drop)
    this.assetManagerOutlet.setDraggedItems(dragIds)

    // If dragging multiple, mark all for visual feedback
    if (dragIds.length > 1) {
      this.#markSelectedForDrag(dragIds)
    }

    document.body.classList.add("is-dragging")
  }

  #handleDragEnd(evt) {
    document.body.classList.remove("is-dragging")
    this.#clearDragMarks()

    // Calculate new position (1-indexed for backend)
    const newIndex = evt.newIndex
    const oldIndex = evt.oldIndex

    if (!this.hasAssetManagerOutlet) {
      return
    }

    // Get the dragged items from asset-manager (set in handleDragStart)
    const assetIds = this.assetManagerOutlet.draggedIds

    // Clear drag state on asset-manager
    this.assetManagerOutlet.clearDraggedItems()

    if (newIndex === oldIndex) return
    if (assetIds.length === 0) return

    // Position is 1-indexed, after the item at newIndex
    const position = newIndex + 1

    this.#sendReorderRequest(assetIds, position)
  }

  #markSelectedForDrag(selectedIds) {
    selectedIds.forEach(id => {
      const row = this.element.querySelector(`[data-asset-item-asset-id-value="${id}"]`)
      if (row) row.classList.add("selected-for-drag")
    })
  }

  #clearDragMarks() {
    this.element.querySelectorAll(".selected-for-drag").forEach(el => {
      el.classList.remove("selected-for-drag")
    })
  }

  async #sendReorderRequest(assetIds, position) {
    if (!this.hasAssetManagerOutlet) return

    const projectId = this.assetManagerOutlet.projectIdValue
    const url = `/projects/${projectId}/assets/bulk_reorder`

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
        },
        body: JSON.stringify({ asset_ids: assetIds, position })
      })

      if (!response.ok) {
        console.error("Reorder failed:", await response.text())
        // Refresh to restore server state
        Turbo.visit(window.location.href, { action: "replace" })
      }
    } catch (error) {
      console.error("Reorder error:", error)
      Turbo.visit(window.location.href, { action: "replace" })
    }
  }
}
